<script setup>
import { createProductSchema } from "~/schemas/Product";
import { roundPlans } from "~/data/plans";

const { data: page } = await useAsyncData(() => 
  queryCollection('RondPage').first()
);

useSeoMeta({
  title: page.value?.metaTitle,
  description: page.value?.metaDescription,
  ogUrl: page.value?.linkCanonical,
  ogTitle: page.value?.title,
  ogDescription: page.value?.description,
});

useHead({
  link: [
    { rel: "canonical", href: page.value?.linkCanonical },
  ],
  script: [
    {
      type: 'application/ld+json',
      textContent: JSON.stringify(createProductSchema({
        name: page.value?.name ?? '',
        img: page.value?.img ?? '',
        description: page.value?.description ?? '',
        url: page.value?.linkCanonical ?? '',
        lowPrice: page.value?.lowPrice ?? '',
        highPrice: page.value?.highPrice ?? '',
        count: page.value?.count ?? '',
      }))
    },
  ],
});

const topCardData = {
  title: page.value?.title,
  desc: page.value?.description,
  img: page.value?.img,
  firstButton: {
    text: "دمو و مشاوره رایگان",
    modalId: "main",
    classes: "!bg-white !text-blue-700 !font-bold",
  },
  secondButton: {
    text: "مشاهده قیمت‌ها",
    scrollTo: "round_price",
  },
};

const isModalShow = ref(false);

const channelInfo = {
  title: "قیمت سرشماره 8 و هزینه هر کانال(بوق آزاد)",
  items: ["خرید شماره از 500,000 تومان", "خرید هر کانال ماهانه 40,000 تومان"],
  desc: [
    "نواتل تنها ارائه کننده خطوط ابری مخابراتی برای استان تهران با پیش شماره 8 می‌باشد.",
    "امکان استفاده و فعال‌سازی این خطوط بر روی سایر سرویس‌های ابری نواتل نیز وجود دارد.",
    "با فعال سازی این خطوط بروی اپلیکیشن اختصاصی نواتل از امکانات متنوع آن بهره‌مند شوید.",
  ],
};

const moreInfoData = {
  id: "1",
  title: "",
  items: [
    {
      id: "1",
      text: "Hosted PBX چیست؟ و چه کاربردی دارد؟",
      img: "/img/articles/link01.webp",
      url: `https://navatel.ir/blog/hosted-pbx/`,
    },
    {
      id: "2",
      text: "شماره ثابت مجازی خود را از نواتل دریافت کنید!",
      img: "/img/articles/link02.webp",
      url: `https://navatel.ir/blog/%d8%b4%d9%85%d8%a7%d8%b1%d9%87-%d8%ab%d8%a7%d8%a8%d8%aa-%d9%85%d8%ac%d8%a7%d8%b2%db%8c-%d9%85%d8%b9%d8%ac%d8%b2%d9%87-%d8%a7%db%8c-%d8%a8%d8%b1%d8%a7%db%8c-%d8%b3%d8%a7%d8%b2%d9%85%d8%a7%d9%86-%d8%b4/`,
    },
    {
      id: "3",
      text: "مقایسه تلفن ابری با تلفن ثابت مخابرات | دو نسل تلفن",
      img: "/img/articles/link03.webp",
      url: `https://navatel.ir/blog/%d9%85%d9%82%d8%a7%db%8c%d8%b3%d9%87-%d8%af%d9%88-%d9%86%d8%b3%d9%84-%d8%aa%d9%84%d9%81%d9%86-%d8%a7%d8%a8%d8%b1%db%8c-%db%8c%d8%a7-%d8%ab%d8%a7%d8%a8%d8%aa/`,
    },
  ],
};
</script>

<template>
  <div class="w-full bg-linear-to-r from-[#012D70] to-[#011C47] pt-5 lg:pt-0 relative overflow-hidden">
    <NuxtImg 
      src="/img/prefix-8-cloud.webp"
      alt="خط تلفن رند ابری پیش‌شماره ۸ تهران"
      class="hidden lg:block absolute left-0 -top-20 h-[150%] z-0 opacity-90"
      loading="lazy"
    />
    <RoundHero :details="topCardData" />
  </div>
  <div class="gridClass">
    <SectionsFeatures 
      :title="page?.features.title"
      :desc="page?.features.desc"
      :features="page?.features.items"
    />
  </div>
  <div class="gridClass">
    <LazySectionsBenefits
      :title="page?.benefits.title"
      :desc="page?.benefits.desc"
      :benefits="page?.benefits.items"
      hydrate-on-visible
    />
  </div>
  <div class="bg-gray-50 pb-10 pt-20">
    <section class="gridClass" id="round_price">
      <h2>
        بسته های مرکز تلفن ابری نواتل
      </h2>
      <div class="mb-10 tablet:flex justify-around gap-4">
        <div
          class="w-full tablet:w-[30%] lg:w-[25%] mt-12 p-4 custom-shadow bg-white relative rounded-xl"
          v-for="item in roundPlans"
          :key="'f' + item.id"
        >
          <ProductPlan :item="item" shopping-src="/online-shopping/buy/" />
        </div>
      </div>
      <div
        class="py-6 px-2 mt-8 bg-gray-200 flex flex-col lg:flex-row justify-center items-center"
      >
        <span class="me-6 font-bold text-black text-sm"
          >در مورد بسته ها نیاز به اطلاعات بیشتر دارید؟</span
        >
        <BaseButton
          @click="isModalShow = true"
          isLean
          size="2xl"
          text="مقایسه بسته ها"
          color="primary"
        />
      </div>
      <SectionModalCompare
        :isShow="isModalShow"
        @close="isModalShow = false"
        :data="metamaPackage"
      />
    </section>
  </div>
  <SectionTablePrice :info="channelInfo" class="mt-16" />
  <SectionTableCompare
    title="تعرفه‌های برقراری تماس با پیش شماره‌های 8280، 8721، 8164"
    tci
    class="mt-16"
  />
  <div class="gridClass">
    <SectionsFAQ
      :title="page?.FAQ.title"
      :items="page?.FAQ.items"
    />
  </div>
  <h2>درباره تلفن ابری بیشتر بدانید</h2>
  <ProductMoreInformation :details="moreInfoData" />
</template>